import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GATEWAY_HTTP_URL } from '../config/api';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
import Breadcrumbs from '../components/Breadcrumbs';

interface AnalyticsOverview {
  totalUsers: number;
  totalProjects: number;
  totalTasks: number;
  totalAutomations: number;
  totalSessions: number;
  recentSessions: number;
  newUsersThisWeek: number;
}

interface TasksByStatus {
  [key: string]: number;
}

interface AdminUser {
  id: string;
  email: string | null;
  name: string | null;
  createdAt: string;
  updatedAt: string;
  projectCount: number;
  automationCount: number;
  sessionCount: number;
  taskCount: number;
  lastActivity: string;
  // Subscription-based billing
  planName: string | null;
  minutesUsed: number | null;
  minutesLimit: number | null;
  minutesRemaining: number | null;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
}

interface AdminTask {
  id: string;
  name: string;
  description: string | null;
  status: string;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  lastRunDuration: number | null;
  queuedAt: string | null;
  hasSchedule: boolean;
  scheduleEnabled: boolean;
  createdAt: string;
  updatedAt: string;
  workflow: { id: string; name: string };
  project: { id: string; name: string };
  user: { id: string; email: string | null; name: string | null };
}

const formatTimeAgo = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
};

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const getStatusColor = (status: string): string => {
  switch (status) {
    case 'idle': return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300';
    case 'queued': return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400';
    case 'running': return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
    case 'completed': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
    case 'failed': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400';
    case 'timeout': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
    default: return 'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300';
  }
};

const Analytics: React.FC = () => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'tasks'>('overview');

  // Overview state
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [tasksByStatus, setTasksByStatus] = useState<TasksByStatus>({});
  const [isLoadingOverview, setIsLoadingOverview] = useState(true);

  // Users state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [userSortColumn, setUserSortColumn] = useState<keyof AdminUser>('createdAt');
  const [userSortDirection, setUserSortDirection] = useState<'asc' | 'desc'>('desc');

  // Tasks state
  const [tasks, setTasks] = useState<AdminTask[]>([]);
  const [isLoadingTasks, setIsLoadingTasks] = useState(false);
  const [taskStatusFilter, setTaskStatusFilter] = useState<string>('');

  // Sort users
  const handleUserSort = (column: keyof AdminUser) => {
    if (userSortColumn === column) {
      setUserSortDirection(userSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortColumn(column);
      setUserSortDirection('desc');
    }
  };

  const sortedUsers = [...users].sort((a, b) => {
    const aVal = a[userSortColumn];
    const bVal = b[userSortColumn];

    if (aVal === null || aVal === undefined) return 1;
    if (bVal === null || bVal === undefined) return -1;

    let comparison = 0;
    if (typeof aVal === 'string' && typeof bVal === 'string') {
      comparison = aVal.localeCompare(bVal);
    } else if (typeof aVal === 'number' && typeof bVal === 'number') {
      comparison = aVal - bVal;
    } else {
      comparison = String(aVal).localeCompare(String(bVal));
    }

    return userSortDirection === 'asc' ? comparison : -comparison;
  });

  // Sort icon component
  const SortIcon = ({ column }: { column: keyof AdminUser }) => {
    if (userSortColumn !== column) {
      return <span className="ml-1 text-gray-400">↕</span>;
    }
    return <span className="ml-1">{userSortDirection === 'asc' ? '↑' : '↓'}</span>;
  };

  // Fetch overview
  useEffect(() => {
    const fetchOverview = async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${GATEWAY_HTTP_URL}/api/admin/analytics`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setOverview(data.data.overview);
          setTasksByStatus(data.data.tasksByStatus);
        }
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setIsLoadingOverview(false);
      }
    };
    fetchOverview();
  }, [getToken]);

  // Fetch users when tab changes
  useEffect(() => {
    if (activeTab === 'users' && users.length === 0) {
      const fetchUsers = async () => {
        setIsLoadingUsers(true);
        try {
          const token = await getToken();
          const response = await fetch(`${GATEWAY_HTTP_URL}/api/admin/users`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setUsers(data.data || []);
          }
        } catch (error) {
          console.error('Failed to fetch users:', error);
        } finally {
          setIsLoadingUsers(false);
        }
      };
      fetchUsers();
    }
  }, [activeTab, getToken, users.length]);

  // Fetch tasks when tab changes or filter changes
  useEffect(() => {
    if (activeTab === 'tasks') {
      const fetchTasks = async () => {
        setIsLoadingTasks(true);
        try {
          const token = await getToken();
          const url = taskStatusFilter
            ? `${GATEWAY_HTTP_URL}/api/admin/tasks?status=${taskStatusFilter}`
            : `${GATEWAY_HTTP_URL}/api/admin/tasks`;
          const response = await fetch(url, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const data = await response.json();
            setTasks(data.data || []);
          }
        } catch (error) {
          console.error('Failed to fetch tasks:', error);
        } finally {
          setIsLoadingTasks(false);
        }
      };
      fetchTasks();
    }
  }, [activeTab, getToken, taskStatusFilter]);

  return (
    <div className="h-full overflow-hidden flex flex-col bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-slate-950 dark:to-slate-900 transition-colors duration-300">
      <TopBar onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)} />

      <div className="flex flex-1 min-h-0 relative">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />

        <div className="flex-1 overflow-y-auto relative">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Breadcrumbs items={[{ label: 'Analytics' }]} className="mb-6" />

            {/* Header */}
            <div className="mb-8">
              <h1 className="text-2xl font-semibold text-gray-900 dark:text-white mb-1">
                Admin Analytics
              </h1>
              <p className="text-sm text-gray-500 dark:text-slate-400">
                System-wide statistics and user management
              </p>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-slate-800 rounded-lg p-1 w-fit">
              {(['overview', 'users', 'tasks'] as const).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                    activeTab === tab
                      ? 'bg-white dark:bg-slate-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div>
                {isLoadingOverview ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : overview ? (
                  <>
                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                      <StatCard label="Total Users" value={overview.totalUsers} icon="users" color="blue" />
                      <StatCard label="Total Projects" value={overview.totalProjects} icon="folder" color="purple" />
                      <StatCard label="Total Tasks" value={overview.totalTasks} icon="tasks" color="green" />
                      <StatCard label="Total Automations" value={overview.totalAutomations} icon="play" color="orange" />
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                      <StatCard label="Total Sessions" value={overview.totalSessions} icon="session" color="cyan" />
                      <StatCard label="Sessions (24h)" value={overview.recentSessions} icon="clock" color="indigo" />
                      <StatCard label="New Users (7d)" value={overview.newUsersThisWeek} icon="userplus" color="emerald" />
                    </div>

                    {/* Tasks by Status */}
                    <div className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 p-6 shadow-sm dark:shadow-none">
                      <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide mb-4">Tasks by Status</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.entries(tasksByStatus).map(([status, count]) => (
                          <div key={status} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-800 rounded-lg">
                            <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(status)}`}>
                              {status}
                            </span>
                            <span className="text-lg font-semibold text-gray-900 dark:text-white">{count}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12 text-gray-500 dark:text-slate-400">
                    Failed to load analytics
                  </div>
                )}
              </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 shadow-sm dark:shadow-none overflow-hidden">
                {isLoadingUsers ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-slate-700">
                          <th onClick={() => handleUserSort('email')} className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-slate-200">
                            User<SortIcon column="email" />
                          </th>
                          <th onClick={() => handleUserSort('planName')} className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-slate-200">
                            Plan<SortIcon column="planName" />
                          </th>
                          <th onClick={() => handleUserSort('minutesRemaining')} className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-slate-200">
                            Minutes<SortIcon column="minutesRemaining" />
                          </th>
                          <th onClick={() => handleUserSort('projectCount')} className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-slate-200">
                            Projects<SortIcon column="projectCount" />
                          </th>
                          <th onClick={() => handleUserSort('taskCount')} className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-slate-200">
                            Tasks<SortIcon column="taskCount" />
                          </th>
                          <th onClick={() => handleUserSort('sessionCount')} className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-slate-200">
                            Sessions<SortIcon column="sessionCount" />
                          </th>
                          <th onClick={() => handleUserSort('createdAt')} className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-slate-200">
                            Joined<SortIcon column="createdAt" />
                          </th>
                          <th onClick={() => handleUserSort('lastActivity')} className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-gray-700 dark:hover:text-slate-200">
                            Last Active<SortIcon column="lastActivity" />
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                        {sortedUsers.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                            <td className="px-6 py-4">
                              <div>
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {user.name || 'No name'}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-slate-400">
                                  {user.email || 'No email'}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center">
                              {user.planName ? (
                                <span className={`px-2 py-1 text-xs font-medium rounded ${
                                  user.planName === 'Pro' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                                  user.planName === 'Team' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                                  'bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300'
                                }`}>
                                  {user.planName}
                                </span>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-center">
                              {user.minutesRemaining !== null ? (
                                <div className="text-sm">
                                  <span className={`font-medium ${
                                    user.minutesRemaining === 0 ? 'text-red-600 dark:text-red-400' :
                                    user.minutesRemaining < 10 ? 'text-yellow-600 dark:text-yellow-400' :
                                    'text-gray-900 dark:text-white'
                                  }`}>
                                    {user.minutesRemaining}
                                  </span>
                                  <span className="text-gray-400 dark:text-slate-500">/{user.minutesLimit}</span>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4 text-center text-sm text-gray-900 dark:text-white">
                              {user.projectCount}
                            </td>
                            <td className="px-6 py-4 text-center text-sm text-gray-900 dark:text-white">
                              {user.taskCount}
                            </td>
                            <td className="px-6 py-4 text-center text-sm text-gray-900 dark:text-white">
                              {user.sessionCount}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-slate-400">
                              {formatDate(user.createdAt)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500 dark:text-slate-400">
                              {formatTimeAgo(user.lastActivity)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Tasks Tab */}
            {activeTab === 'tasks' && (
              <div>
                {/* Filter */}
                <div className="mb-4 flex items-center space-x-4">
                  <label className="text-sm text-gray-600 dark:text-slate-400">Filter by status:</label>
                  <select
                    value={taskStatusFilter}
                    onChange={(e) => setTaskStatusFilter(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-white"
                  >
                    <option value="">All</option>
                    <option value="idle">Idle</option>
                    <option value="queued">Queued</option>
                    <option value="running">Running</option>
                    <option value="completed">Completed</option>
                    <option value="failed">Failed</option>
                  </select>
                </div>

                <div className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 shadow-sm dark:shadow-none overflow-hidden">
                  {isLoadingTasks ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-slate-700">
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Task</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">User</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Last Run</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Scheduled</th>
                            <th className="px-6 py-3 text-center text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                          {tasks.map((task) => (
                            <tr key={task.id} className="hover:bg-gray-50 dark:hover:bg-slate-800/50">
                              <td className="px-6 py-4">
                                <div>
                                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                                    {task.name}
                                  </div>
                                  <div className="text-xs text-gray-500 dark:text-slate-400">
                                    {task.project.name} / {task.workflow.name}
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900 dark:text-white">
                                  {task.user.name || task.user.email || 'Unknown'}
                                </div>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <span className={`px-2 py-1 text-xs font-medium rounded ${getStatusColor(task.status)}`}>
                                  {task.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-gray-500 dark:text-slate-400">
                                {task.lastRunAt ? formatTimeAgo(task.lastRunAt) : 'Never'}
                                {task.lastRunStatus && (
                                  <span className={`ml-2 text-xs ${task.lastRunStatus === 'success' ? 'text-green-600' : 'text-red-600'}`}>
                                    ({task.lastRunStatus})
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-center">
                                {task.hasSchedule ? (
                                  <span className={`text-xs ${task.scheduleEnabled ? 'text-green-600' : 'text-gray-400'}`}>
                                    {task.scheduleEnabled ? 'Active' : 'Disabled'}
                                  </span>
                                ) : (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-center">
                                <button
                                  onClick={() => navigate(`/project/${task.project.id}`)}
                                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                >
                                  <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                  </svg>
                                  View Project
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Stat Card Component
const StatCard: React.FC<{
  label: string;
  value: number;
  icon: string;
  color: string;
}> = ({ label, value, icon, color }) => {
  const getIcon = () => {
    switch (icon) {
      case 'users':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        );
      case 'folder':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
        );
      case 'tasks':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'play':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'session':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'clock':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'userplus':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
          </svg>
        );
      default:
        return null;
    }
  };

  const colorClasses: Record<string, string> = {
    blue: 'bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-400',
    purple: 'bg-purple-50 dark:bg-purple-500/10 text-purple-600 dark:text-purple-400',
    green: 'bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-400',
    orange: 'bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400',
    cyan: 'bg-cyan-50 dark:bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
    indigo: 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400',
    emerald: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  };

  return (
    <div className="bg-white dark:bg-slate-900/80 rounded-xl border border-gray-200 dark:border-slate-700/50 p-5 shadow-sm dark:shadow-none">
      <div className="flex items-center justify-between mb-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          {getIcon()}
        </div>
      </div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
      <div className="text-xs text-gray-500 dark:text-slate-400 font-medium">{label}</div>
    </div>
  );
};

export default Analytics;
